import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { Operator, OperatorRequest } from './types';
import { createOperator, deleteOperator, getOperator, getOperators, updateOperator } from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetOperators(
  params?: any,
  options?: UseQueryOptions<{ data: Operator[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: Operator[]; meta: MetaApiResponse }>({
    queryKey: ['operators', params],
    queryFn: () => getOperators(params),
    ...options
  });
}

export function useGetOperator(operatorId: string, options?: UseQueryOptions<Operator, Error>) {
  return useQuery<Operator>({
    queryKey: ['operator', operatorId],
    queryFn: () => getOperator(operatorId!),
    enabled: Boolean(operatorId),
    ...options
  });
}

export function useCreateOrUpdateOperator(hasToNavigate: boolean = true) {
  const navigate = useNavigate();
  const { operatorId } = useParams<{ operatorId: string }>();

  return useMutation({
    mutationFn: (payload: OperatorRequest) =>
      operatorId ? updateOperator(operatorId!, payload) : createOperator(payload),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['operators']
      });
      queryClient.invalidateQueries({
        queryKey: ['operator', operatorId]
      });
      if (hasToNavigate) {
        navigate(-1);
      }
    }
  });
}

export function useDeleteOperator() {
  return useMutation({
    mutationFn: (id: string) => deleteOperator(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['operators'],
        refetchType: 'active'
      });
    }
  });
}
